/**
 * Centralized logging utility with environment-based log levels
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  OFF = 4,
}

class Logger {
  private level: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
    
    // Set log level based on environment
    this.level = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
    
    // Allow override via environment variable
    const envLogLevel = import.meta.env.VITE_LOG_LEVEL;
    if (envLogLevel && envLogLevel in LogLevel) {
      this.level = LogLevel[envLogLevel as keyof typeof LogLevel];
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.level;
  }

  private formatMessage(level: string, message: string, context?: string): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? `[${context}]` : '';
    return `[${timestamp}] [${level}] ${contextStr} ${message}`;
  }

  debug(message: string, context?: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('DEBUG', message, context), ...args);
    }
  }

  info(message: string, context?: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage('INFO', message, context), ...args);
    }
  }

  warn(message: string, context?: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message, context), ...args);
    }
  }

  error(message: string, context?: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', message, context), ...args);
    }
  }

  // Development-only logging - only shows in development mode
  dev(message: string, context?: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      console.log(this.formatMessage('DEV', message, context), ...args);
    }
  }

  // Always log regardless of level (for critical errors)
  critical(message: string, context?: string, ...args: unknown[]): void {
    console.error(this.formatMessage('CRITICAL', message, context), ...args);
  }

  // Helper for component rendering logs
  render(componentName: string, props?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.debug(`Rendering ${componentName}`, 'RENDER', props);
    }
  }

  // Helper for API requests
  api(method: string, url: string, details?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.debug(`${method} ${url}`, 'API', details);
    }
  }

  // Helper for auth operations
  auth(operation: string, details?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.debug(operation, 'AUTH', details);
    }
  }

  // Set log level dynamically
  setLevel(level: LogLevel): void {
    this.level = level;
  }

  // Get current log level
  getLevel(): LogLevel {
    return this.level;
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions for backward compatibility
export const log = {
  debug: (message: string, context?: string, ...args: unknown[]) => logger.debug(message, context, ...args),
  info: (message: string, context?: string, ...args: unknown[]) => logger.info(message, context, ...args),
  warn: (message: string, context?: string, ...args: unknown[]) => logger.warn(message, context, ...args),
  error: (message: string, context?: string, ...args: unknown[]) => logger.error(message, context, ...args),
  dev: (message: string, context?: string, ...args: unknown[]) => logger.dev(message, context, ...args),
  critical: (message: string, context?: string, ...args: unknown[]) => logger.critical(message, context, ...args),
  render: (componentName: string, props?: Record<string, unknown>) => logger.render(componentName, props),
  api: (method: string, url: string, details?: Record<string, unknown>) => logger.api(method, url, details),
  auth: (operation: string, details?: Record<string, unknown>) => logger.auth(operation, details),
};

export default logger;